import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { analytics } from '../services/analyticsService';
import { Link } from 'react-router-dom';


const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const rawFaq = t('home.faq.questions', { returnObjects: true });
  const faqList: Array<{ question: string; answer: string }> = Array.isArray(rawFaq) ? rawFaq : [];

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="bg-slate-50 dark:bg-slate-900 py-12 md:py-20">
      <div className="mx-auto max-w-5xl px-4">
        <div className="flex flex-col items-center gap-12 md:flex-row md:gap-16 lg:gap-24">
          {/* 左侧说明与行动按钮 */}
          <div className="md:w-1/2 text-center md:text-left">
            <span className="inline-block py-1 px-3 mb-3 text-xs font-semibold uppercase tracking-wider text-orange-600 bg-orange-100 dark:bg-orange-950/60 dark:text-orange-400 rounded-full">
              {t('home.badge', 'Built for Indie Game Devs & 2D Artists')}
            </span>
            <div className="flex flex-col space-y-2">
              <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-4xl md:text-5xl">
                {t('home.title')}
              </h1>
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-amber-500 to-orange-500 dark:from-orange-400 dark:via-amber-300 dark:to-orange-400 leading-snug">
                {t('home.subtitle')}
              </h2>
            </div>
            
            <p className="mt-6 text-base md:text-lg leading-relaxed text-gray-600 dark:text-gray-300">
              {t('home.description')}
            </p>

            {/* CTA 按钮组 */}
            <div className="mt-8 flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start">
              <Link
                to="/pricing"
                onClick={() => analytics.trackEvent('hero_cta_click', { target: 'pricing', label: 'get_started' })}
                className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl bg-orange-500 px-7 py-3.5 text-center text-base sm:text-lg font-bold text-white shadow-lg shadow-orange-500/25 hover:bg-orange-600 transition-all transform active:scale-95 whitespace-nowrap"
              >
                {t('home.getStarted')}
              </Link>
              <Link
                to="/download"
                onClick={() => analytics.trackEvent('hero_cta_click', { target: 'download', label: 'download_client' })}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-3.5 text-center text-base sm:text-lg font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/80 hover:border-gray-400 dark:hover:border-gray-600 shadow-sm transition-all whitespace-nowrap"
              >
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {t('home.downloadClient')}
              </Link>
            </div>

            {/* 引擎与平台支持标签 */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-800 flex flex-wrap items-center justify-center md:justify-start gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
              <span className="bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded">Unity</span>
              <span className="bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded">Godot 4</span>
              <span className="bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded">Cocos2d</span>
              <span className="bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded">Phaser 3</span>
              <span className="bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded">TexturePacker</span>
              <span className="bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded">LibGDX</span>
              <span className="bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded">Starling</span>
            </div>
          </div>

          {/* 右侧真实客户端运行窗口预览 */}
          <div className="md:w-1/2 flex justify-center w-full">
            <div className="relative w-full max-w-lg lg:max-w-xl group">
              {/* 纯净外框与柔和立体阴影 */}
              <div className="overflow-hidden rounded-2xl border border-gray-200/90 dark:border-gray-700/80 shadow-2xl transition-all duration-500 ease-out transform group-hover:scale-[1.05] group-hover:-translate-y-1.5 group-hover:shadow-3xl group-hover:shadow-orange-500/15 cursor-pointer">
                <img
                  src="/images/hero-app-preview.png"
                  alt="AtlasSlicer 2D Desktop Workspace Preview"
                  className="w-full h-auto block"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 核心特性 4 格区块 */}
      <section className="py-12 md:py-20">
        <div className="mx-auto max-w-5xl px-4">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {/* Card 1: Rust 原生打包 */}
            <div className="rounded-xl bg-orange-50 p-6 shadow-lg dark:bg-orange-950/30 border border-orange-100 dark:border-orange-900/40 relative overflow-hidden">
              <div className="relative z-10">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500 text-white shadow-md">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('home.features.fast')}</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{t('home.features.fastDesc')}</p>
              </div>
            </div>

            {/* Card 2: 4合1 智能切片 */}
            <div className="rounded-xl bg-teal-50 p-6 shadow-lg dark:bg-teal-950/30 border border-teal-100 dark:border-teal-900/40 relative overflow-hidden">
              <div className="relative z-10">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-teal-500 text-white shadow-md">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879a3 3 0 11-4.242-4.242L10.5 5.5m0 0l-7 7m7-7v14" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('home.features.accurate')}</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{t('home.features.accurateDesc')}</p>
              </div>
            </div>

            {/* Card 3: 动效质检播放器 */}
            <div className="rounded-xl bg-purple-50 p-6 shadow-lg dark:bg-purple-950/30 border border-purple-100 dark:border-purple-900/40 relative overflow-hidden">
              <div className="relative z-10">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-purple-600 text-white shadow-md">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('home.features.flexible')}</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{t('home.features.flexibleDesc')}</p>
              </div>
            </div>

            {/* Card 4: 10大引擎与离线安全 */}
            <div className="rounded-xl bg-blue-50 p-6 shadow-lg dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/40 relative overflow-hidden">
              <div className="relative z-10">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500 text-white shadow-md">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('home.features.safe')}</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{t('home.features.safeDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ 问答区块 */}
      <section className="py-12 md:py-16 bg-white dark:bg-gray-800">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t('home.faq.title')}
          </h2>
          
          <div className="space-y-4 max-w-3xl mx-auto">
            {faqList.map((faq, index: number) => (
              <div 
                key={index} 
                className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="flex justify-between items-center w-full px-6 py-4 text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-950 transition-colors"
                >
                  <span className="font-semibold text-gray-900 dark:text-white">{faq.question}</span>
                  <svg 
                    className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {expandedFaq === index && (
                  <div className="px-6 py-4 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm md:text-base">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
